import { useState } from 'react';
import { toast } from 'react-toastify';
import { Dialog, DialogActions, DialogButton, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import { chatStore } from '~/lib/stores/chat';
import { projectStore } from '~/lib/stores/project';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

interface DeleteAllChatsDialogProps {
  open: boolean;
  onClose: VoidFunction;
}

export const DeleteAllChatsDialog = ({ open, onClose }: DeleteAllChatsDialogProps) => {
  const [deletingChats, setDeletingChats] = useState(false);

  const handleDeleteAllChats = async () => {
    if (deletingChats) {
      return;
    }

    try {
      /**
       * We show a spinner if deletion takes longer than expected.
       * This should provide visual feedback for potentially slow
       * operations.
       */
      await withSpinner({
        action: async () => {
          await chatStore.removeAllChats();

          if (projectStore.getSlug() !== undefined) {
            // we have to hard reload the page because the user has a chat open which no longer exist at this point
            window.location.pathname = '/';
          }

          toast.success('All chats deleted');
        },
        onStateUpdate: setDeletingChats,
      });
    } catch (error) {
      toast.error('Failed to delete chats');
      logger.error(error);
    } finally {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={450}>
      <DialogTitle>Delete all chats?</DialogTitle>
      <DialogDescription>
        <p>You are about to delete all chats.</p>
        <p className="mt-1">This cannot be undone. Would you like to proceed?</p>
      </DialogDescription>
      <DialogActions>
        <DialogButton variant="secondary" onClick={onClose}>
          Cancel
        </DialogButton>
        <DialogButton variant="danger" disabled={deletingChats} onClick={handleDeleteAllChats}>
          {deletingChats ? (
            <div className="flex items-center gap-1.5">
              <div className="i-svg-spinners:90-ring-with-bg text-lg" />
              Deleting all
            </div>
          ) : (
            'Delete all'
          )}
        </DialogButton>
      </DialogActions>
    </Dialog>
  );
};
